import React, { useState, useCallback } from 'react';
import clsx from 'clsx';

import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import EditIcon from '@mui/icons-material/Edit';
import TrashIcon from '@mui/icons-material/DeleteForeverOutlined';
import { useModal } from 'hooks/useModal';
import { WarningPopUp } from 'components/@shared/WarningPopUp';
import { ITableHeadCell, ITableComponents } from '../types';
// import { useStyles } from './styles';
import Tooltip from '@mui/material/Tooltip';

interface Props<T> {
    rows: T[];
    columns: ITableHeadCell[];
    components: ITableComponents<T>;
    deleteWarning?: React.ReactNode;
    warningHeader?: string;
    selected: string[];
    withCheckbox: boolean;
    onEditAction?: (rowId: string) => void;
    onDeleteAction?: (rowId: string) => void;
    onSelectRow: (e: React.MouseEvent<HTMLTableCellElement>, rowId: string) => void;
}

const EnhancedTableBody = <T extends { id: string }>({
    rows,
    columns,
    components,
    deleteWarning,
    warningHeader,
    selected,
    withCheckbox,
    onEditAction,
    onDeleteAction,
    onSelectRow,
}: Props<T>) => {
    // const classes = useStyles();

    const [deleteId, setDeleteId] = useState('');

    const { open, openModal, closeModal } = useModal();

    const editHandler = (id: string) => (event: React.MouseEvent) => {
        event.preventDefault();
        if (onEditAction) onEditAction(id);
    };

    const deleteHandler = useCallback(
        () => {
            if (onDeleteAction && deleteId) {
                onDeleteAction(deleteId);
                closeModal();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onDeleteAction, deleteId],
    );

    const deleteItemConfirm = useCallback(
        (id: string) => (event: React.MouseEvent) => {
            event.preventDefault();
            setDeleteId(id);
            openModal();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onDeleteAction],
    );

    const selectRowHandler = useCallback(
        (rowId: string) => (event: React.MouseEvent<HTMLTableCellElement>) => onSelectRow(event, rowId),
        [onSelectRow],
    );

    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    return (
        <TableBody>
            {rows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                    <TableRow
                        key={row.id}
                        tabIndex={-1}
                        role={withCheckbox ? 'checkbox' : 'none'}
                        aria-checked={isItemSelected}
                        selected={isItemSelected}
                    >
                        {components.Row ? (
                            <components.Row rowData={row} />
                        ) : (
                            <>
                                {withCheckbox && (
                                    <TableCell padding="checkbox" onClick={selectRowHandler(row.id)}>
                                        <Checkbox
                                            color="primary"
                                            checked={isItemSelected}
                                            inputProps={{
                                                'aria-labelledby': labelId,
                                            }}
                                        />
                                    </TableCell>
                                )}
                                {columns.map((item: ITableHeadCell) => (
                                    <TableCell align="left" key={item.field}>
                                        {row[item.field as keyof typeof row] as React.ReactNode}
                                    </TableCell>
                                ))}
                            </>
                        )}
                        {components.Actions ? (
                            <TableCell>
                                <components.Actions rowData={row} />
                            </TableCell>
                        ) : (
                            (!!onEditAction || !!onDeleteAction) && (
                                <TableCell>
                                    <div>
                                        <Tooltip title="Edit">
                                            <IconButton onClick={editHandler(row.id)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton onClick={deleteItemConfirm(row.id)}>
                                                <TrashIcon color="error" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </TableCell>
                            )
                        )}
                    </TableRow>
                );
            })}
            <WarningPopUp
                isOpen={open}
                onClose={closeModal}
                onConfirm={deleteHandler}
                warningBody={deleteWarning}
                warningHeader={warningHeader}
                confirmButtonText="Delete"
            />
        </TableBody>
    );
};

export default EnhancedTableBody;
